import React, { useId } from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, Check } from 'lucide-react';
import { AppCard, AppCardContent, EmptyState, StatusBadge, cn } from '../../../ui';
import CardHead from './CardHead';
import ClassControl from './ClassControl';
import { formatTime, isOverdue, modeLabel, plural } from '../trainerDashboardUtils';

/** The live class and the next one are the two a trainer acts on from here. */
const isFocus = (item) => item.state === 'live' || item.state === 'next';

function AgendaRow({ item, now, onStart, onEnd }) {
  const focus = isFocus(item);
  const done = item.state === 'done';
  const meta = [
    item.course,
    modeLabel(item.mode),
    item.studentCount != null ? plural(item.studentCount, 'student', 'students') : '',
  ].filter(Boolean).join(' · ');

  return (
    <li
      className={cn(
        'tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-rounded-md tw-px-3 tw-py-2.5',
        focus && 'tw-bg-primary-soft',
      )}
    >
      <div className="tw-w-[4.75rem] tw-shrink-0 tw-text-xs tw-leading-snug">
        {item.startTime ? (
          <time
            dateTime={item.startTime}
            className={cn('tw-block tw-font-semibold', done ? 'tw-text-content-subtle' : 'tw-text-content')}
          >
            {formatTime(item.startTime)}
          </time>
        ) : (
          <span className="tw-block tw-font-semibold tw-text-content-subtle">Time TBD</span>
        )}
        {item.endTime && (
          <time dateTime={item.endTime} className="tw-block tw-text-content-subtle">
            {formatTime(item.endTime)}
          </time>
        )}
      </div>

      <div className="tw-min-w-0 tw-flex-[1_1_12rem]">
        <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
          <Link
            to={`/i/batch/${item.id}`}
            className={cn(
              'tw-min-w-0 tw-truncate tw-rounded-sm tw-text-sm tw-font-semibold tw-no-underline tw-focus-ring hover:tw-text-primary',
              done ? 'tw-text-content-muted' : 'tw-text-content',
            )}
          >
            {item.name}
          </Link>
          {item.state === 'live' && <StatusBadge status="live" domain="batch" />}
          {item.state === 'next' && (
            <span className="tw-text-[0.6875rem] tw-font-semibold tw-text-primary-soft-fg">
              {isOverdue(item, now) ? 'Due now' : 'Up next'}
            </span>
          )}
          {done && (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[0.6875rem] tw-font-medium tw-text-content-subtle">
              <Check size={12} aria-hidden="true" /> Done
            </span>
          )}
        </div>
        {meta && <p className="tw-m-0 tw-mt-0.5 tw-truncate tw-text-xs tw-text-content-muted">{meta}</p>}
      </div>

      {focus && item.batch && (
        <div className="tw-ml-auto tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-gap-2">
          <ClassControl batch={item.batch} emphasis onStart={onStart} onEnd={onEnd} />
        </div>
      )}
    </li>
  );
}

/**
 * TodayAgendaCard — the day's classes in order, with the live or next one
 * raised and its Start / Rejoin button on it. That button is the reason most
 * trainers open this page, so it is the only filled button in the list.
 *
 * Props:
 *   agenda     — trainerDashboardUtils.buildAgenda()
 *   isTomorrow — the server fell back to tomorrow because today has nothing
 *   dayName    — the weekday the schedule is for
 *   now        — "HH:MM", the page's clock
 *   onStart / onEnd — live-class handlers from the page
 */
const TodayAgendaCard = ({ agenda = [], isTomorrow = false, dayName, now, onStart, onEnd }) => {
  const titleId = useId();
  const description = isTomorrow
    ? `Nothing today — ${dayName || 'tomorrow'}'s classes`
    : agenda.length ? plural(agenda.length, 'class', 'classes') : 'Your timetable for the day';

  return (
    <AppCard as="section" aria-labelledby={titleId}>
      <CardHead
        id={titleId}
        title={isTomorrow ? 'Tomorrow' : 'Today'}
        description={description}
        linkTo="/i/calendar"
        linkLabel="Calendar"
      />
      <AppCardContent>
        {agenda.length === 0 ? (
          <EmptyState
            icon={CalendarCheck}
            title="No classes today"
            description="Nothing is scheduled for today or tomorrow."
          />
        ) : (
          <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0">
            {agenda.map((item) => (
              <AgendaRow key={item.id} item={item} now={now} onStart={onStart} onEnd={onEnd} />
            ))}
          </ol>
        )}
      </AppCardContent>
    </AppCard>
  );
};

export default TodayAgendaCard;
